import React, { useContext, useEffect, useState } from 'react'
import { Keyboard, LayoutAnimation, Platform, Pressable, StyleSheet, UIManager, View } from 'react-native'
import { Icon, Layout, Text, useTheme } from '@ui-kitten/components'
import { SafeAreaInsetsContext } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { useSupportConversation } from '../../global/Support/useSupportConversation'
import MessageList from './components/MessageList'
import MessageInput from './components/MessageInput'

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true)
}

// Track the keyboard height ourselves. KeyboardAvoidingView mis-measures inside an iOS `pageSheet`
// modal (its frame is in the sheet's window space while the keyboard frame is in screen space), so
// the composer ended up half-hidden. Lifting the content by the real keyboard height is reliable.
function useKeyboardHeight() {
  const [height, setHeight] = useState(0)
  useEffect(() => {
    if (Platform.OS === 'web') return undefined
    const showEvt = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvt = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'
    const onShow = (e) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      setHeight(e?.endCoordinates?.height ?? 0)
    }
    const onHide = () => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
      setHeight(0)
    }
    const s = Keyboard.addListener(showEvt, onShow)
    const h = Keyboard.addListener(hideEvt, onHide)
    return () => {
      s.remove()
      h.remove()
    }
  }, [])
  return height
}

export default function SupportChatPanel({ onBack, onOpenProductDraft, onQuickReply }) {
  const theme = useTheme()
  const { t } = useTranslation()
  // Read insets via context (NOT useSafeAreaInsets, which THROWS when no SafeAreaProvider is in
  // context). This panel renders inside a RN <Modal> (pageSheet); if the provider doesn't reach
  // across the modal boundary, useSafeAreaInsets() would throw during render and hard-crash the
  // app — and a fatal render crash terminates before Sentry can flush, so it left no event. Reading
  // the context directly is null-safe: undefined ⇒ fall back to a fixed gap.
  const insets = useContext(SafeAreaInsetsContext)
  const keyboardHeight = useKeyboardHeight()
  const { messages, loading, sending, error, send } = useSupportConversation()

  // Keyboard open ⇒ lift the composer to just above it. Keyboard closed ⇒ keep a bottom gap so the
  // input isn't flush with the screen edge (home indicator on notched devices, a small gap on the
  // rest). Web has no keyboard inset and no safe area, so this stays 0 there (web already looks ok).
  const safeBottom = insets?.bottom ?? 0
  const bottomGap = Math.max(safeBottom, Platform.OS === 'web' ? 0 : 12)
  const bottomInset = keyboardHeight > 0 ? keyboardHeight : bottomGap

  return (
    <Layout style={styles.flex} level="2">
      {/* Branded header, consistent with the help panel's primary-blue top bar */}
      <View style={[styles.header, { backgroundColor: theme['color-primary-500'] }]}>
        <Pressable
          onPress={onBack}
          hitSlop={14}
          style={({ pressed }) => [styles.backBtn, pressed && styles.pressed]}
          accessibilityRole="button"
          accessibilityLabel={t('support.back')}
        >
          <Icon name="arrow-back-outline" width={24} height={24} fill="#fff" />
        </Pressable>
        <View style={styles.avatar}>
          <Icon name="headphones-outline" width={20} height={20} fill="#fff" />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.headerTitle}>{t('support.support')}</Text>
          <Text style={styles.headerSub}>{t('support.replyTime')}</Text>
        </View>
      </View>

      <View style={[styles.flex, { paddingBottom: bottomInset }]}>
        {error ? (
          <View style={[styles.errorBar, { backgroundColor: theme['color-danger-100'] }]}>
            <Icon name="alert-triangle-outline" width={14} height={14} fill={theme['color-danger-600']} />
            <Text category="c1" style={[styles.errorText, { color: theme['color-danger-700'] }]} numberOfLines={2}>
              {t(error)}
            </Text>
          </View>
        ) : null}
        <MessageList
          messages={messages}
          loading={loading}
          onOpenProductDraft={onOpenProductDraft}
          onQuickReply={onQuickReply}
        />
        <MessageInput onSend={send} sending={sending} />
      </View>
    </Layout>
  )
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    // generous top pad clears the modal's absolute close button; right pad keeps title off it
    paddingTop: Platform.OS === 'web' ? 22 : 16,
    paddingBottom: 16,
    paddingLeft: 12,
    paddingRight: 56,
  },
  backBtn: { padding: 6, borderRadius: 18, marginRight: 2 },
  pressed: { opacity: 0.6 },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  headerText: { flex: 1 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '700', lineHeight: 22 },
  headerSub: { color: 'rgba(255,255,255,0.82)', fontSize: 12, lineHeight: 16, marginTop: 1 },
  errorBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    gap: 6,
  },
  errorText: { flex: 1 },
})
